// Batch Credit Note spread — Jul-2026 global CN design (client-approved).
// The CN total is entered ONCE against the batch (mirroring real payment
// advices); a configurable policy (FIFO today) spreads it across invoice
// headroom. The preview is fully transparent and editable (manual override)
// before it fills the per-invoice allocation state — posting & reversal
// reuse the untouched invoice-level engine (zero GL involvement).
import { useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Card } from "@/components/ui-helpers";
import { toast } from "sonner";
import { CheckCircle2, Layers, Loader2, Wand2, X } from "lucide-react";

export function BatchCnSpread({ clientId, cnRows, invoices, allocs, adjDeltaFor, retentionTargetsFor, applied, onApply, onClear }) {
  const [sel, setSel] = useState({}); // cn_id -> batch amount string
  const [policy, setPolicy] = useState("fifo");
  const [policies, setPolicies] = useState([{ key: "fifo", label: "FIFO — oldest invoice first" }]);
  const [preview, setPreview] = useState(null);
  const [edits, setEdits] = useState({}); // `${invoice_id}|${cn_id}` -> amount string
  const [loading, setLoading] = useState(false);

  const usable = (cnRows || []).filter((c) => !c.legacy_full_allocation && c.available > 0.005);
  if (!usable.length) return null;

  const entries = Object.entries(sel)
    .map(([cn_id, raw]) => ({ cn_id, amount: Math.round((Number(raw) || 0) * 100) / 100 }))
    .filter((e) => e.amount > 0);
  const batchTotal = Math.round(entries.reduce((s, e) => s + e.amount, 0) * 100) / 100;
  const overCn = entries.filter((e) => {
    const cn = usable.find((c) => c.cn_id === e.cn_id);
    return cn && e.amount > cn.available + 0.01;
  });

  const toggleCn = (cn) => setSel((s) => {
    const next = { ...s };
    if (next[cn.cn_id] !== undefined) delete next[cn.cn_id];
    else next[cn.cn_id] = String(cn.available);
    return next;
  });

  async function runPreview() {
    if (!entries.length) return toast.error("Select at least one Credit Note and enter its batch amount");
    if (overCn.length) return toast.error("A Credit Note batch amount exceeds its available balance");
    setLoading(true);
    try {
      const { data } = await api.post("/bulk-receipts/cn-spread-preview", {
        client_id: clientId, policy,
        batch_cns: entries,
        allocations: invoices.map((inv) => ({
          invoice_id: inv.id,
          amount: Number(allocs[inv.id]?.amount) || 0,
          tds_deducted: Number(allocs[inv.id]?.tds) || 0,
          adj_delta: adjDeltaFor(inv),
          retention_targets: retentionTargetsFor ? retentionTargetsFor(inv) : {},
        })),
      });
      setPreview(data);
      setEdits({});
      if (data.policies?.length) setPolicies(data.policies);
      if (data.unallocated > 0.005)
        toast.warning(`${formatINR(data.unallocated)} of the CN batch could not be spread — not enough invoice headroom`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to compute CN spread preview");
    } finally { setLoading(false); }
  }

  const autoAmt = (row, cnId) => (row.cn_parts || []).find((p) => p.cn_id === cnId)?.amount || 0;
  const cellVal = (invId, cnId, auto) => {
    const k = `${invId}|${cnId}`;
    return edits[k] !== undefined ? (Number(edits[k]) || 0) : auto;
  };
  const isOverridden = Object.keys(edits).some((k) => {
    const [invId, cnId] = k.split("|");
    const row = (preview?.rows || []).find((r) => r.invoice_id === invId);
    return row && Math.abs((Number(edits[k]) || 0) - autoAmt(row, cnId)) > 0.005;
  });

  const previewRows = (preview?.rows || []).filter((r) =>
    (r.cn_parts || []).length > 0
    || Object.entries(edits).some(([k, v]) => k.startsWith(`${r.invoice_id}|`) && (Number(v) || 0) > 0)
    || r.headroom > 0.005);

  const distributed = {};
  (preview?.rows || []).forEach((r) => {
    entries.forEach((e) => {
      const v = cellVal(r.invoice_id, e.cn_id, autoAmt(r, e.cn_id));
      if (v > 0) distributed[e.cn_id] = Math.round(((distributed[e.cn_id] || 0) + v) * 100) / 100;
    });
  });
  const distributedTotal = Math.round(Object.values(distributed).reduce((s, v) => s + v, 0) * 100) / 100;
  const cnOverBatch = entries.filter((e) => (distributed[e.cn_id] || 0) > e.amount + 0.01);
  const rowOverHeadroom = (preview?.rows || []).filter((r) => {
    const t = entries.reduce((s, e) => s + cellVal(r.invoice_id, e.cn_id, autoAmt(r, e.cn_id)), 0);
    return t > r.headroom + 0.01;
  });

  function apply() {
    if (cnOverBatch.length) return toast.error(`Override total for a Credit Note exceeds its batch amount (${cnOverBatch[0].cn_id ? usable.find((c) => c.cn_id === cnOverBatch[0].cn_id)?.note_number || "" : ""})`);
    if (rowOverHeadroom.length) return toast.error(`${rowOverHeadroom[0].invoice_number}: CN override exceeds the invoice headroom`);
    const mapping = {};
    (preview?.rows || []).forEach((r) => {
      entries.forEach((e) => {
        const v = cellVal(r.invoice_id, e.cn_id, autoAmt(r, e.cn_id));
        if (v > 0) mapping[r.invoice_id] = { ...(mapping[r.invoice_id] || {}), [e.cn_id]: String(v) };
      });
    });
    if (!Object.keys(mapping).length) return toast.error("Nothing to apply — the spread is empty");
    const mode = isOverridden ? "manual_override" : "auto";
    onApply(mapping, { policy, mode, entries: entries.map((e) => ({ ...e })) });
    toast.success(mode === "auto"
      ? `Batch CN spread applied — ${policies.find((p) => p.key === policy)?.label || policy}`
      : "Batch CN spread applied with manual override");
  }

  return (
    <Card className="p-4 mb-4 border-violet-200" data-testid="batch-cn-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-violet-700">
          <Layers size={13} /> Batch Credit Notes — global adjustment
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="batch-cn-policy" className="border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:border-violet-500"
            value={policy} onChange={(e) => setPolicy(e.target.value)}>
            {policies.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          <button data-testid="batch-cn-preview-btn" onClick={runPreview} disabled={loading || !entries.length || overCn.length > 0}
            className="flex items-center gap-1.5 border border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 disabled:opacity-50 px-3 py-1.5 text-xs uppercase tracking-wider font-bold">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} Preview Spread
          </button>
        </div>
      </div>
      <div className="text-[11px] text-slate-500 mb-3">
        Deduct Credit Notes against the batch total (as on the client's payment advice) — the selected policy
        spreads them across invoice headroom. Review the spread below, override any amount if needed, then apply.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {usable.map((cn) => {
          const on = sel[cn.cn_id] !== undefined;
          const amt = Number(sel[cn.cn_id]) || 0;
          const over = on && amt > cn.available + 0.01;
          return (
            <div key={cn.cn_id} className={`border p-2 ${over ? "border-rose-300 bg-rose-50/40" : on ? "border-violet-300 bg-violet-50/40" : "border-slate-200 bg-white"}`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input data-testid={`batch-cn-toggle-${cn.note_number}`} type="checkbox" checked={on} onChange={() => toggleCn(cn)} />
                <span className="text-[10px] font-bold text-violet-700">{cn.note_number}</span>
                <span className="text-[10px] text-slate-400 ml-auto">{cn.note_date} · Available {formatINR(cn.available)}</span>
              </label>
              {on && (
                <input data-testid={`batch-cn-amount-${cn.note_number}`} type="number" min="0" step="0.01"
                  className={`w-full mt-1.5 border px-2 py-1.5 text-right text-xs focus:outline-none ${over ? "border-rose-400 bg-rose-50" : "border-slate-300 focus:border-violet-500"}`}
                  value={sel[cn.cn_id]} onChange={(e) => setSel((s) => ({ ...s, [cn.cn_id]: e.target.value }))} />
              )}
            </div>
          );
        })}
      </div>
      {entries.length > 0 && (
        <div className="mt-2 text-xs text-slate-600" data-testid="batch-cn-total">
          Batch CN total: <span className="font-bold text-violet-700">{formatINR(batchTotal)}</span>
          {overCn.length > 0 && <span className="text-rose-600 font-bold ml-2">A CN amount exceeds its available balance!</span>}
        </div>
      )}

      {preview && (
        <div className="mt-3 border border-slate-200" data-testid="batch-cn-preview-table">
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Proposed spread — {preview.policy_label}{isOverridden && <span className="text-amber-700 ml-1">(manual override)</span>}
            </div>
            <div className="text-[11px] text-slate-600">
              Spread {formatINR(distributedTotal)} of {formatINR(batchTotal)}
              {preview.unallocated > 0.005 && !isOverridden && (
                <span className="text-amber-700 font-bold ml-1" data-testid="batch-cn-unallocated">· {formatINR(preview.unallocated)} unallocated (no headroom)</span>
              )}
            </div>
          </div>
          <div className="overflow-x-auto max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0">
                <tr className="bg-slate-100 text-slate-600">
                  {["Invoice", "Date", "Outstanding", "Cash + TDS", "Retention Reserved", "Headroom",
                    ...entries.map((e) => usable.find((c) => c.cn_id === e.cn_id)?.note_number || "CN"), "Row CN Total"].map((h, i) => (
                    <th key={i} className="px-2.5 py-2 text-left text-[10px] uppercase tracking-wider whitespace-nowrap bg-slate-100">{h}</th>))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((r) => {
                  const rowTotal = entries.reduce((s, e) => s + cellVal(r.invoice_id, e.cn_id, autoAmt(r, e.cn_id)), 0);
                  const overRow = rowTotal > r.headroom + 0.01;
                  return (
                    <tr key={r.invoice_id} className={`border-b border-slate-50 ${overRow ? "bg-rose-50/60" : rowTotal > 0 ? "bg-violet-50/30" : ""}`}>
                      <td className="px-2.5 py-1.5 font-semibold whitespace-nowrap">{r.invoice_number}</td>
                      <td className="px-2.5 py-1.5 whitespace-nowrap">{r.invoice_date}</td>
                      <td className="px-2.5 py-1.5 text-right tabular-nums">{formatINR(r.outstanding)}</td>
                      <td className="px-2.5 py-1.5 text-right tabular-nums">{formatINR(Math.round((r.cash + r.tds_deducting) * 100) / 100)}</td>
                      <td className="px-2.5 py-1.5 text-right tabular-nums text-amber-700" data-testid={`batch-cn-reserved-${r.invoice_number}`}>
                        {formatINR(r.retention_reserved || 0)}
                      </td>
                      <td className="px-2.5 py-1.5 text-right tabular-nums font-bold">{formatINR(r.headroom)}</td>
                      {entries.map((e) => {
                        const cn = usable.find((c) => c.cn_id === e.cn_id);
                        const k = `${r.invoice_id}|${e.cn_id}`;
                        const val = edits[k] !== undefined ? edits[k] : (autoAmt(r, e.cn_id) || "");
                        return (
                          <td key={e.cn_id} className="px-2.5 py-1.5">
                            <input data-testid={`batch-cn-cell-${r.invoice_number}-${cn?.note_number || e.cn_id}`}
                              type="number" min="0" step="0.01"
                              className={`w-24 border px-2 py-1 text-right text-xs focus:outline-none ${overRow ? "border-rose-400 bg-rose-50" : "border-slate-300 focus:border-violet-500"}`}
                              value={val} placeholder="0"
                              onChange={(ev) => setEdits((x) => ({ ...x, [k]: ev.target.value }))} />
                          </td>
                        );
                      })}
                      <td className={`px-2.5 py-1.5 text-right tabular-nums font-bold ${overRow ? "text-rose-600" : "text-violet-700"}`}>
                        {formatINR(Math.round(rowTotal * 100) / 100)}{overRow && " !"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 border-t border-slate-200 flex items-center justify-between flex-wrap gap-2">
            <div className="text-[11px]">
              {cnOverBatch.length > 0 && <span className="text-rose-600 font-bold">A CN's override total exceeds its batch amount. </span>}
              {rowOverHeadroom.length > 0 && <span className="text-rose-600 font-bold">An invoice's CN total exceeds its headroom. </span>}
              {!cnOverBatch.length && !rowOverHeadroom.length && (
                <span className="text-slate-500">Every amount is editable — edits switch the record to “manual override” for the audit trail.</span>
              )}
            </div>
            <button data-testid="batch-cn-apply-btn" onClick={apply}
              disabled={cnOverBatch.length > 0 || rowOverHeadroom.length > 0}
              className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold">
              <CheckCircle2 size={13} /> Apply Spread to Voucher
            </button>
          </div>
        </div>
      )}

      {applied && (
        <div className="mt-3 border border-emerald-300 bg-emerald-50 px-3 py-2 flex items-center gap-2 text-xs text-emerald-800" data-testid="batch-cn-applied-banner">
          <CheckCircle2 size={14} />
          <span className="flex-1">
            Batch CN spread applied — <b>{applied.mode === "auto" ? (policies.find((p) => p.key === applied.policy)?.label || applied.policy) : "manual override"}</b>,
            {" "}{formatINR(Math.round(applied.entries.reduce((s, e) => s + e.amount, 0) * 100) / 100)} across the invoices below.
            The distribution is recorded permanently on the voucher's audit trail.
          </span>
          <button data-testid="batch-cn-clear-btn" onClick={onClear}
            className="flex items-center gap-1 border border-emerald-400 text-emerald-800 hover:bg-emerald-100 px-2 py-1 text-[10px] uppercase tracking-wider font-bold">
            <X size={11} /> Clear
          </button>
        </div>
      )}
    </Card>
  );
}
